/**
 * Google Tag Manager + event tracking.
 *
 * The GTM container is loaded lazily once `REACT_APP_GTM_ID` is set
 * (e.g. `GTM-ABC123`). When the id is blank, every call here becomes
 * a no-op, so the site runs identically without analytics wired up.
 *
 * All CTA events push to `window.dataLayer`, which GTM forwards to
 * GA4 (and any other tag you add inside the GTM container).
 */

const GTM_ID = process.env.REACT_APP_GTM_ID || "";

let initialized = false;

export function isEnabled() {
  return Boolean(GTM_ID);
}

export function initGTM() {
  if (initialized || typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  if (!GTM_ID) return; // leave dataLayer ready but don't load the container
  initialized = true;

  // GTM standard snippet — head portion
  window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(s);

  // <noscript> iframe fallback (body portion)
  const noscript = document.createElement("noscript");
  const iframe = document.createElement("iframe");
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
  iframe.height = "0";
  iframe.width = "0";
  iframe.style.display = "none";
  iframe.style.visibility = "hidden";
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

export function trackEvent(name, params = {}) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: name, ...params });
}

export function trackPageView(path, title) {
  trackEvent("page_view", {
    page_path: path,
    page_title: title || (typeof document !== "undefined" ? document.title : ""),
    page_location: typeof window !== "undefined" ? window.location.href : "",
  });
}

/**
 * Attach a single delegated click listener that turns every CTA link
 * into an analytics event — no need to edit individual buttons.
 *
 * Handles:
 *   - Any <a href*="/book-appointment">  → `book_cta_click`
 *   - Any <a href^="tel:">                → `phone_click`
 *   - Any <a href*="wa.me">               → `whatsapp_click`
 */
export function attachCtaListeners() {
  if (typeof document === "undefined") return;
  document.addEventListener("click", (e) => {
    const a = e.target && e.target.closest && e.target.closest("a");
    if (!a) return;
    const href = a.getAttribute("href") || "";
    const testId = a.getAttribute("data-testid") || "";
    const label = (a.innerText || "").trim().slice(0, 80);

    if (href.includes("/book-appointment") || testId === "nri-book") {
      // Video-call CTA in the NRI section has its own dedicated event
      if (testId === "nri-book") {
        trackEvent("nri_video_call_click", { label, test_id: testId });
      }
      trackEvent("book_cta_click", { label, test_id: testId, href });
      return;
    }
    if (href.startsWith("tel:")) {
      trackEvent("phone_click", { number: href.replace(/^tel:/, ""), label });
      return;
    }
    if (href.includes("wa.me") || href.includes("whatsapp.com")) {
      const where =
        testId === "whatsapp-float" ? "floating"
        : testId === "nri-whatsapp" ? "nri"
        : "inline";
      trackEvent("whatsapp_click", { location: where, test_id: testId });
    }
  });
}
